import React, { useState, useEffect, useCallback, useMemo } from 'react';

// URL Aset Gambar
const LOGO_URL = `https://content-horizon.b-cdn.net/venom-logo.png`;
const FAVICON_URL = `https://content-horizon.b-cdn.net/venom-logo.png`;
const XP_BG = `https://content-horizon.b-cdn.net/90278.jpg`; // Wallpaper kustom yang diminta

// Helper untuk membungkus URL dengan CORS Proxy
const getProxyUrl = (url) => `https://corsproxy.io/?${encodeURIComponent(url)}`;

// --- KOMPONEN DI LUAR APP UNTUK MENCEGAH KEHILANGAN FOKUS ---

const ScanLines = () => (
  <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden opacity-15">
    <div className="absolute inset-0 scanlines-effect"></div>
  </div>
);

const XPWindow = ({ title, children, icon = "fa-folder", onClose, isCollapsed, onToggleCollapse }) => (
  <div className={`xp-window shadow-xl mb-6 flex flex-col transition-all duration-200 ${isCollapsed ? 'h-auto' : ''}`}>
    <div className="xp-title-bar flex justify-between items-center px-2 py-1 select-none">
      <div className="flex items-center gap-2 cursor-default" onClick={onToggleCollapse}>
        <i className={`fa-solid ${icon} text-white text-xs drop-shadow-md`}></i>
        <span className="text-white font-bold text-sm drop-shadow-md tracking-wide">{title}</span>
      </div>
      <div className="flex gap-1">
        <button onClick={onToggleCollapse} className="xp-btn-min w-5 h-5 flex items-center justify-center text-white font-bold text-xs" title={isCollapsed ? "Expand" : "Collapse"}>
          {isCollapsed ? '□' : '_'}
        </button>
        {onClose && <button onClick={onClose} className="xp-btn-close w-5 h-5 flex items-center justify-center text-white font-bold text-xs">×</button>}
      </div>
    </div>
    {!isCollapsed && (
      <div className="xp-content bg-[#ece9d8] p-4 flex-grow border-l-2 border-r-2 border-b-2 border-[#0058e4] animate-fade-in-xp">
        {children}
      </div>
    )}
  </div>
);

// --- KOMPONEN UTAMA ---

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pinInput, setPinInput] = useState(``);
  const [workerUrl, setWorkerUrl] = useState(``);
  const [useProxy, setUseProxy] = useState(false); 
  const [links, setLinks] = useState([]); 
  const [longUrl, setLongUrl] = useState(``);
  const [customShort, setCustomShort] = useState(``);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditingUrl, setIsEditingUrl] = useState(false);
  const [copyStatus, setCopyStatus] = useState(null);

  const [isServerConfigCollapsed, setIsServerConfigCollapsed] = useState(true);

  // State untuk pengurutan
  const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'desc' });

  const CORRECT_PIN = `998789`;
  const [searchTerm, setSearchTerm] = useState(``);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const [editingLink, setEditingLink] = useState(null);
  const [editNewLongUrl, setEditNewLongUrl] = useState(``);
  const [deletingLink, setDeletingLink] = useState(null);

  const getRequestUrl = (targetUrl) => {
    return useProxy ? getProxyUrl(targetUrl) : targetUrl;
  };

  const copyToClipboard = (shortCode) => {
    const fullUrl = `${workerUrl.trim().replace(/\/+$/, ``)}/${shortCode}`;
    const textArea = document.createElement("textarea");
    textArea.value = fullUrl;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      setCopyStatus(shortCode);
      setTimeout(() => setCopyStatus(null), 2000);
    } catch (err) {
      console.error('Gagal menyalin:', err);
    }
    document.body.removeChild(textArea);
  };

  useEffect(() => {
    const faLink = document.createElement(`link`);
    faLink.rel = `stylesheet`;
    faLink.href = `https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css`;
    document.head.appendChild(faLink);

    const faviconLink = document.createElement(`link`);
    faviconLink.rel = `icon`;
    faviconLink.href = FAVICON_URL;
    document.head.appendChild(faviconLink);

    const authStatus = sessionStorage.getItem(`venom_auth`);
    if (authStatus === `true`) setIsAuthenticated(true);

    return () => {
      document.head.removeChild(faLink);
      document.head.removeChild(faviconLink);
    };
  }, []);

  const fetchLinks = useCallback(async () => {
    if (!workerUrl) return;
    setLoading(true);
    setError(null);
    try {
      const cleanUrl = workerUrl.trim().replace(/\/+$/, ``);
      const targetUrl = `${cleanUrl}/api/links`;
      const response = await fetch(getRequestUrl(targetUrl));
      
      if (!response.ok) {
        if (response.status === 403) throw new Error(`Akses Ditolak (403): Silakan coba aktifkan Proxy.`);
        throw new Error(`Status Server: ${response.status}`);
      }
      
      const data = await response.json();
      setLinks(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [workerUrl, useProxy]);

  useEffect(() => {
    const savedUrl = localStorage.getItem(`workerUrl`);
    const savedProxy = localStorage.getItem(`useProxy`);
    setWorkerUrl(savedUrl || `https://kasihmasuk.space`);
    if (savedProxy !== null) setUseProxy(savedProxy === 'true');
  }, []);

  useEffect(() => {
    if (workerUrl && isAuthenticated) fetchLinks();
  }, [workerUrl, isAuthenticated, fetchLinks]);

  const handleUrlSave = () => {
    localStorage.setItem('workerUrl', workerUrl.trim());
    localStorage.setItem('useProxy', useProxy.toString());
    setIsEditingUrl(false);
    fetchLinks();
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (pinInput === CORRECT_PIN) {
      setIsAuthenticated(true);
      sessionStorage.setItem(`venom_auth`, `true`);
      setError(null);
    } else {
      setError(`PIN yang dimasukkan tidak valid.`);
    }
    setPinInput(``);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem(`venom_auth`);
    setLinks([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const cleanUrl = workerUrl.trim().replace(/\/+$/, ``);
      const targetUrl = `${cleanUrl}/api/links`;
      const response = await fetch(getRequestUrl(targetUrl), {
        method: `POST`,
        headers: { 'Content-Type': `application/json` },
        body: JSON.stringify({ 
          longUrl, 
          shortCode: customShort || null, 
          timestamp: Date.now() 
        }),
      });
      if (!response.ok) throw new Error(`Gagal menambahkan tautan.`);
      setLongUrl(``); setCustomShort(``); fetchLinks();
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  const confirmDelete = async () => {
    if (!deletingLink) return;
    setLoading(true);
    try {
      const cleanUrl = workerUrl.trim().replace(/\/+$/, ``);
      const targetUrl = `${cleanUrl}/api/links`;
      const response = await fetch(getRequestUrl(targetUrl), {
        method: `DELETE`,
        headers: { 'Content-Type': `application/json` },
        body: JSON.stringify({ shortCode: deletingLink.shortCode }),
      });
      if (!response.ok) throw new Error(`Gagal menghapus tautan.`);
      setDeletingLink(null); await fetchLinks();
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingLink) return;
    setLoading(true);
    try {
      const cleanUrl = workerUrl.trim().replace(/\/+$/, ``);
      const targetUrl = `${cleanUrl}/api/links`;
      const response = await fetch(getRequestUrl(targetUrl), {
        method: `PUT`,
        headers: { 'Content-Type': `application/json` },
        body: JSON.stringify({ 
          shortCode: editingLink.shortCode, 
          newLongUrl: editNewLongUrl, 
          timestamp: Date.now() 
        }),
      });
      if (!response.ok) throw new Error(`Gagal memperbarui tautan.`);
      setEditingLink(null); fetchLinks();
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  // Fungsi untuk memicu pengurutan
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Logika Filter dan Sort Data
  const sortedAndFilteredLinks = useMemo(() => {
    let result = [...links].filter(link => 
      link.shortCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      link.longUrl.toLowerCase().includes(searchTerm.toLowerCase())
    );

    result.sort((a, b) => {
      const valA = sortConfig.key === 'shortCode' ? a.shortCode.toLowerCase() : (a[sortConfig.key] || 0);
      const valB = sortConfig.key === 'shortCode' ? b.shortCode.toLowerCase() : (b[sortConfig.key] || 0);

      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [links, searchTerm, sortConfig]);

  const totalPages = Math.ceil(sortedAndFilteredLinks.length / itemsPerPage);
  const currentItems = sortedAndFilteredLinks.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const formatDate = (ts) => {
    if (!ts) return "---";
    const d = new Date(ts);
    return d.toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit', year: '2-digit' }) + " " + d.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? '▴' : '▾';
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#5a7edc] flex items-center justify-center font-sans">
        <ScanLines />
        <div className="w-full flex flex-col">
          <div className="h-[2px] bg-gradient-to-r from-transparent via-[#ffffff55] to-transparent w-full"></div>
          <div className="flex items-center justify-center py-20 gap-20">
            <div className="text-right border-r-2 border-[#ffffff33] pr-10">
              <img src={LOGO_URL} alt="Venom" className="h-24 mb-4 ml-auto" />
              <h1 className="text-white text-4xl font-light italic">Selamat Datang</h1>
            </div>
            <div className="pl-10 max-w-xs w-full">
              <p className="text-white mb-6 text-sm">Masukkan PIN Administrator untuk mengelola tautan Anda.</p>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white rounded border-2 border-blue-400 p-1 flex items-center justify-center shadow-inner">
                        <i className="fa-solid fa-user-shield text-blue-500 text-2xl"></i>
                    </div>
                    <div className="flex flex-col flex-grow">
                        <span className="text-white font-bold text-lg leading-tight">Admin</span>
                        <input 
                            type="password" 
                            className="bg-white border-2 border-blue-400 px-2 py-0.5 text-black w-full text-sm outline-none focus:ring-1 focus:ring-orange-400"
                            value={pinInput}
                            onChange={(e) => setPinInput(e.target.value)}
                            placeholder="Ketik PIN"
                            autoFocus
                        />
                    </div>
                    <button type="submit" className="bg-[#38b000] border-2 border-white rounded-full p-2 w-10 h-10 flex items-center justify-center text-white shadow-lg hover:brightness-110 active:scale-95">
                        <i className="fa-solid fa-arrow-right"></i>
                    </button>
                </div>
                {error && <div className="text-red-200 text-xs font-bold bg-red-700/30 p-2 rounded">{error}</div>}
              </form>
            </div>
          </div>
          <div className="h-[2px] bg-gradient-to-r from-transparent via-[#ffffff55] to-transparent w-full mt-auto"></div>
          <div className="p-10 flex justify-between items-center">
            <div className="flex items-center gap-2 text-white opacity-80 cursor-pointer hover:opacity-100">
                <div className="bg-red-600 rounded-full w-6 h-6 flex items-center justify-center">
                    <i className="fa-solid fa-power-off text-[10px]"></i>
                </div>
                <span className="text-sm font-bold">Matikan Komputer</span>
            </div>
            <p className="text-white text-xs opacity-50 tracking-widest uppercase">Venom Link Manager v3.2.2</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cover bg-center font-sans p-4 md:p-8" style={{ backgroundImage: `url(${XP_BG})` }}>
      <ScanLines />
      <div className="max-w-6xl mx-auto pb-16">
        
        {/* Taskbar Look Header */}
        <div className="flex justify-between items-center mb-8 bg-[#245edb] p-3 rounded-t-lg border-b-2 border-white/20 shadow-lg">
          <div className="flex items-center gap-4">
             <img src={LOGO_URL} alt="Logo" className="h-8" />
             <div className="h-6 w-[1px] bg-white/20"></div>
             <p className="text-white font-bold text-sm tracking-wide">Administrator Online</p>
          </div>
          <button onClick={handleLogout} className="bg-gradient-to-b from-red-400 to-red-600 border border-white px-4 py-1 text-white font-bold text-xs rounded hover:brightness-110 transition-all shadow-md">
            Log Off
          </button>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          
          <div className="lg:col-span-1 space-y-6">
            <XPWindow 
              title="Pengaturan Server" 
              icon="fa-gears" 
              isCollapsed={isServerConfigCollapsed} 
              onToggleCollapse={() => setIsServerConfigCollapsed(!isServerConfigCollapsed)}
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-bold text-[#1e1e1e]">API Worker</span>
                <label className="flex items-center gap-2 cursor-pointer text-[10px]">
                  <span>Proxy:</span>
                  <input 
                    type="checkbox" 
                    checked={useProxy}
                    onChange={(e) => { setUseProxy(e.target.checked); localStorage.setItem('useProxy', e.target.checked.toString()); }}
                    className="cursor-pointer"
                  />
                </label>
              </div>
              {isEditingUrl ? (
                <div className="space-y-3">
                  <input type="text" value={workerUrl} onChange={(e) => setWorkerUrl(e.target.value)} className="w-full bg-white border border-gray-400 p-1 text-xs outline-none focus:border-blue-500" />
                  <button onClick={handleUrlSave} className="xp-classic-btn w-full py-1 text-xs font-bold">Simpan Alamat</button>
                </div>
              ) : (
                <div className="bg-white border border-gray-300 p-2 rounded flex flex-col gap-2">
                  <span className="text-[10px] break-all text-gray-600 font-mono italic">{workerUrl}</span>
                  <button onClick={() => setIsEditingUrl(true)} className="text-[10px] text-blue-700 font-bold hover:underline text-right">Ubah Konfigurasi</button>
                </div>
              )}
            </XPWindow>

            <XPWindow title="Tambah Tautan" icon="fa-plus-square" isCollapsed={false} onToggleCollapse={() => {}}>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-gray-700">URL Tujuan</label>
                  <input type="url" value={longUrl} onChange={(e) => setLongUrl(e.target.value)} required className="w-full bg-white border border-gray-400 p-1 text-xs outline-none focus:ring-1 focus:ring-orange-400" placeholder="https://..." />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-gray-700">Kode Shortlink (Opsional)</label>
                  <div className="flex items-center bg-white border border-gray-400">
                    <span className="px-2 text-xs text-gray-400 bg-gray-100 border-r border-gray-400">/</span>
                    <input type="text" value={customShort} onChange={(e) => setCustomShort(e.target.value)} className="flex-grow p-1 text-xs outline-none uppercase" placeholder="ACARA-2026" />
                  </div>
                </div>
                <button type="submit" disabled={loading || !workerUrl} className="xp-classic-btn w-full py-2 font-bold text-sm bg-gradient-to-b from-blue-400 to-blue-600 text-white border border-white">
                   {loading ? 'Proses...' : 'Tambahkan Tautan'}
                </button>
              </form>
            </XPWindow>
          </div>

          <div className="lg:col-span-2">
            <XPWindow title="Eksplorer Tautan" icon="fa-display" isCollapsed={false} onToggleCollapse={() => {}}>
              <div className="flex justify-between items-center mb-4 bg-white/50 p-2 border border-gray-300 rounded">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold">Pencarian:</span>
                  <input 
                    type="text" 
                    className="bg-white border border-gray-400 px-2 py-0.5 text-xs outline-none w-32 md:w-64" 
                    value={searchTerm} 
                    onChange={(e) => setSearchTerm(e.target.value)} 
                  />
                </div>
                <button onClick={fetchLinks} className="text-xs text-blue-700 hover:underline font-bold">
                  <i className={`fa-solid fa-rotate-right mr-1 ${loading ? 'fa-spin' : ''}`}></i> Segarkan
                </button>
              </div>

              <div className="bg-white border border-gray-400 overflow-x-auto min-h-[400px]">
                <table className="w-full text-left text-xs border-collapse">
                  <thead className="bg-[#f0f0f0] border-b border-gray-400 select-none">
                    <tr>
                      <th 
                        className="py-2 px-3 border-r border-gray-300 font-normal cursor-pointer hover:bg-gray-200"
                        onClick={() => requestSort('shortCode')}
                      >
                        Nama {getSortIcon('shortCode')}
                      </th>
                      <th className="py-2 px-3 border-r border-gray-300 font-normal">URL Asli</th>
                      <th 
                        className="py-2 px-3 border-r border-gray-300 font-normal text-center cursor-pointer hover:bg-gray-200"
                        onClick={() => requestSort('timestamp')}
                      >
                        Modifikasi {getSortIcon('timestamp')}
                      </th>
                      <th className="py-2 px-3 font-normal text-center">Menu</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.length === 0 ? (
                      <tr><td colSpan="4" className="py-20 text-center text-gray-400 italic">Folder ini kosong.</td></tr>
                    ) : (
                      currentItems.map((link) => (
                        <tr key={link.shortCode} className="hover:bg-[#3169c6] hover:text-white group">
                          <td className="py-1 px-3 border-r border-gray-100 group-hover:border-[#3169c6]">
                            <button onClick={() => copyToClipboard(link.shortCode)} className="flex items-center gap-2 w-full text-left font-bold">
                              <i className="fa-solid fa-file-export text-blue-500 group-hover:text-white"></i>
                              /{link.shortCode}
                              {copyStatus === link.shortCode && <span className="bg-yellow-400 text-black text-[8px] px-1 rounded animate-pulse">Copied!</span>}
                            </button>
                          </td>
                          <td className="py-1 px-3 border-r border-gray-100 group-hover:border-[#3169c6] max-w-[200px] truncate opacity-70 italic">
                            {link.longUrl}
                          </td>
                          <td className="py-1 px-3 border-r border-gray-100 group-hover:border-[#3169c6] text-center text-[10px]">
                            {formatDate(link.timestamp)}
                          </td>
                          <td className="py-1 px-3 text-center">
                             <div className="flex justify-center gap-3">
                                <button onClick={() => { setEditingLink(link); setEditNewLongUrl(link.longUrl); }} className="hover:text-yellow-200"><i className="fa-solid fa-pen-to-square"></i></button>
                                <button onClick={() => setDeletingLink(link)} className="hover:text-red-200"><i className="fa-solid fa-trash-can"></i></button>
                             </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="mt-4 flex justify-between items-center text-[10px]">
                  <p className="text-gray-600">Halaman {currentPage} dari {totalPages}</p>
                  <div className="flex gap-1">
                    <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} className="xp-classic-btn px-2">{"<"} Prev</button>
                    <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} className="xp-classic-btn px-2">Next {">"}</button>
                  </div>
                </div>
              )}
            </XPWindow>
          </div>
        </div>
      </div>

      {/* XP Style Modals */}
      {editingLink && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-6 backdrop-blur-sm">
          <div className="max-w-xl w-full">
            <XPWindow title="Properti Tautan" icon="fa-file-signature" onClose={() => setEditingLink(null)}>
              <form onSubmit={handleEditSubmit} className="space-y-6">
                <div className="flex gap-4">
                    <div className="w-16 h-16 bg-white border border-gray-400 p-2 flex items-center justify-center">
                        <i className="fa-solid fa-link text-blue-500 text-3xl"></i>
                    </div>
                    <div className="flex flex-col justify-center">
                        <h4 className="font-bold text-sm">Ganti Alamat Tautan</h4>
                        <p className="text-[10px] text-gray-600">ID: /{editingLink.shortCode}</p>
                    </div>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold">Masukkan Alamat Baru:</label>
                  <textarea required rows="4" className="w-full bg-white border border-gray-400 p-3 text-xs outline-none focus:ring-1 focus:ring-blue-400" value={editNewLongUrl} onChange={(e) => setEditNewLongUrl(e.target.value)} />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                    <button type="button" onClick={() => setEditingLink(null)} className="xp-classic-btn px-6 py-1 text-xs">Batal</button>
                    <button type="submit" disabled={loading} className="xp-classic-btn px-6 py-1 text-xs font-bold bg-[#38b000] text-white">Simpan</button>
                </div>
              </form>
            </XPWindow>
          </div>
        </div>
      )}

      {deletingLink && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[110] p-6 backdrop-blur-sm">
           <div className="max-w-sm w-full">
            <XPWindow title="Konfirmasi Penghapusan" icon="fa-circle-exclamation" onClose={() => setDeletingLink(null)}>
                <div className="flex gap-4 mb-6">
                    <i className="fa-solid fa-circle-question text-red-500 text-4xl"></i>
                    <p className="text-xs leading-relaxed">Apakah Anda yakin ingin menghapus <b>/{deletingLink.shortCode}</b> secara permanen?</p>
                </div>
                <div className="flex justify-center gap-3">
                    <button onClick={confirmDelete} disabled={loading} className="xp-classic-btn px-8 py-1 text-xs font-bold text-white bg-red-600">Hapus</button>
                    <button onClick={() => setDeletingLink(null)} disabled={loading} className="xp-classic-btn px-8 py-1 text-xs">Batal</button>
                </div>
            </XPWindow>
           </div>
        </div>
      )}
      
      <style>{`
        .xp-window { border-radius: 8px 8px 0 0; overflow: hidden; }
        .xp-title-bar {
          background: linear-gradient(to bottom, #0058e4 0%, #3b93ff 9%, #0055e5 18%, #0055e5 92%, #0230a8 100%);
        }
        .xp-btn-close {
          background: linear-gradient(to bottom, #e7674b 0%, #d43d1f 10%, #d53c1e 15%, #d43d1f 25%, #d13a1d 30%, #cd371c 45%, #c12c19 50%, #c12c19 60%, #9a1e12 100%);
          border: 1px solid #7a0d04; border-radius: 3px;
        }
        .xp-btn-min, .xp-btn-max {
          background: linear-gradient(to bottom, #0058e4 0%, #3b93ff 9%, #0055e5 18%, #0055e5 92%, #0230a8 100%);
          border: 1px solid #ffffffaa; border-radius: 3px;
        }
        .xp-classic-btn {
          background: #efefef; border: 1px solid #7f9db9; border-radius: 3px; 
          box-shadow: inset 0 1px 0 white, 0 1px 2px rgba(0,0,0,0.1); color: #1e1e1e;
          cursor: pointer;
        }
        .xp-classic-btn:hover { background: #e5f1fb; border-color: #0054e3; }
        .xp-classic-btn:active { background: #d5e1eb; box-shadow: inset 0 2px 4px rgba(0,0,0,0.1); }
        .custom-scrollbar::-webkit-scrollbar { width: 12px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #f1f1f1; border-left: 1px solid #d4d0c8; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #d4d0c8; border: 1px solid #ffffff; box-shadow: inset -1px -1px #808080, inset 1px 1px #ffffff; }
        
        .scanlines-effect {
          background: linear-gradient(rgba(18, 16, 16, 0) 50%, rgba(0, 0, 0, 0.1) 50%);
          background-size: 100% 4px;
        }

        @keyframes fade-in-xp {
          from { opacity: 0; transform: translateY(-5px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-xp { animation: fade-in-xp 0.2s ease-out; }
      `}</style>
    </div>
  );
}